import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BUSINESS_PLAN_TYPES, IBusinessToolType } from 'constants/businessPlan';
import { DIMENSIONALITY_VALUES, IDimensionalityOption } from 'constants/businessTool';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import { icons } from 'PDF/assets/icons';
import { IChartWithBars } from 'PDF/components/Charts/types';
import { IDonutWithText } from 'PDF/components/Charts/types';
import { IFeaturesTableImageInfo, IFeaturesTableRowItem } from 'PDF/components/FeaturesTable';
import { IPointTableProps } from 'PDF/components/PointTable';
import { IStripedTableRow } from 'PDF/components/StripedTable';
import { formatValueWithUnit } from 'PDF/helpers/chartFormatters';
import { getColorStyles, getColorStylesByRecord, getColorType } from 'PDF/helpers/documentStyle';
import { formatDateForPDF } from 'PDF/helpers/formatDates';
import { formatCurrency } from 'PDF/helpers/formatMoneyPDF';
import { formatPercentage } from 'PDF/helpers/formatPercentagePDF';
import { getMagazineTextObject } from 'PDF/helpers/pdfMapping';

import {
  COMPANY_QUALITIES_NAMES,
  IKeyCompetitors,
} from 'components/BusinessToolForm/Sections/MarketAnalysis/CompetitiveAnalysis/utils/form';
import {
  IAveragePricesRegionRow,
  IMarketTrendsForm,
  IRegionConsumptionRow,
  MARKET_TRENDS_FIELD_NAMES,
  REGION_PERIODS_NAMES,
} from 'components/BusinessToolForm/Sections/MarketAnalysis/MarketAnalysisOverview/utils/form';
import { IBusinessToolForm } from 'components/BusinessToolForm/utils/form';

import { getCurrencySymbol } from 'helpers/getCurrencySymbol';
import { formatNumber, formatNumberToShort } from 'helpers/numbers';

import {
  colorIndexesByPalleteForecast,
  colorIndexesMarketTrends,
  colorIndexesPriceOverview,
  colorIndexesStructureDetails,
} from './chartColors';
import { getDimensionalityForExamplePage, getGenderForExamplePage } from './helpers';
import { getPriorityForExamplePage } from '../AboutTheCompany/helpers';

const dimensionalityAmount = {
  [DIMENSIONALITY_VALUES.SINGLE]: 1,
  [DIMENSIONALITY_VALUES.THOUSAND]: 1000,
  [DIMENSIONALITY_VALUES.MILLION]: 1000000,
  [DIMENSIONALITY_VALUES.BILLION]: 1000000000,
};

export const getMagazineItems = (
  businessPlanChapters: IBusinessToolForm,
  businessPlanType: IBusinessToolType,
  isExamplePage: boolean,
) => {
  const {
    competitiveAnalysis: {
      competitors,
      attractingCompetitorsCustomers,
      competitorsDifference,
      purchasingProducts,
      futureExpectations,
      barriers,
      changingOfCurrentMarket,
    },
    consumerNeeds: {
      purchasingDecision,
      influencingFactors,
      differences,
      howCustomersSatisfied,
      demand,
      customerBaseSize,
      survey,
    },
    marketOverview: {
      mainMarketOverview,
      servicesProductsSummary,
      consumersDescription,
      marketCompetitors,
      actualAndForecastedConsumption,
      marketShare,
      salesMethod,
      marketStructureDescription,
      marketGrowthFactors,
      technologyImpact,
      priceStrategies,
    },
  } = businessPlanChapters.MARKET_ANALYSIS;

  if (businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS) {
    return [
      [
        getMagazineTextObject(
          isExamplePage ? 'Market Segmentation. ' : t('pdfPages.mappings.marketAnalysis.marketSegmentation'),
          mainMarketOverview,
        ),
        getMagazineTextObject(
          isExamplePage ? 'Product Summary. ' : t('pdfPages.mappings.marketAnalysis.productSummary'),
          servicesProductsSummary,
        ),
        getMagazineTextObject(
          isExamplePage ? 'Consumption Analysis. ' : t('pdfPages.mappings.marketAnalysis.consumptionAnalysis'),
          actualAndForecastedConsumption,
        ),
      ],
      [
        getMagazineTextObject(
          isExamplePage ? 'Sales Strategy. ' : t('pdfPages.mappings.marketAnalysis.salesStrategy'),
          salesMethod,
        ),
        getMagazineTextObject(
          isExamplePage ? 'Structure Overview. ' : t('pdfPages.mappings.marketAnalysis.structureOverview'),
          marketStructureDescription,
        ),
      ],
      [
        getMagazineTextObject(
          isExamplePage ? 'Growth Factors. ' : t('pdfPages.mappings.marketAnalysis.growthFactors'),
          marketGrowthFactors,
        ),
        getMagazineTextObject(
          isExamplePage ? 'Pricing Strategies. ' : t('pdfPages.mappings.marketAnalysis.pricingStrategies'),
          priceStrategies,
        ),
      ],
      [
        getMagazineTextObject(
          isExamplePage
            ? 'Competitor Identification. '
            : t('pdfPages.mappings.marketAnalysis.competitorIdentification'),
          competitors,
        ),
      ],
      [
        getMagazineTextObject(
          isExamplePage ? 'Entry Barriers. ' : t('pdfPages.mappings.marketAnalysis.entryBarriers'),
          barriers,
        ),
      ],
      [
        getMagazineTextObject(
          isExamplePage ? 'Purchase Decisions. ' : t('pdfPages.mappings.marketAnalysis.purchaseDecisions'),
          purchasingDecision,
        ),
        getMagazineTextObject(
          isExamplePage ? 'Market Satisfaction. ' : t('pdfPages.mappings.marketAnalysis.marketSatisfaction'),
          howCustomersSatisfied,
        ),
        getMagazineTextObject(
          isExamplePage ? 'Demand Creation. ' : t('pdfPages.mappings.marketAnalysis.demandCreation'),
          demand,
        ),
        getMagazineTextObject(
          isExamplePage ? 'Customer Base Size. ' : t('pdfPages.mappings.marketAnalysis.customerBaseSize'),
          customerBaseSize,
        ),
      ],
    ];
  }

  return [
    // Market Overview
    [
      getMagazineTextObject(
        isExamplePage ? 'Market Segmentation. ' : t('pdfPages.mappings.marketAnalysis.marketSegmentation'),
        mainMarketOverview,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Product Summary. ' : t('pdfPages.mappings.marketAnalysis.productSummary'),
        servicesProductsSummary,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Consumer Demographics. ' : t('pdfPages.mappings.marketAnalysis.consumerDemographics'),
        consumersDescription,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Market Competitors. ' : t('pdfPages.mappings.marketAnalysis.marketCompetitors'),
        marketCompetitors,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Consumption Analysis. ' : t('pdfPages.mappings.marketAnalysis.consumptionAnalysis'),
        actualAndForecastedConsumption,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Market Share Insight. ' : t('pdfPages.mappings.marketAnalysis.marketShareInsight'),
        marketShare,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Sales Strategy. ' : t('pdfPages.mappings.marketAnalysis.salesStrategy'),
        salesMethod,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Structure Overview. ' : t('pdfPages.mappings.marketAnalysis.structureOverview'),
        marketStructureDescription,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Growth Factors. ' : t('pdfPages.mappings.marketAnalysis.growthFactors'),
        marketGrowthFactors,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Technology Impact. ' : t('pdfPages.mappings.marketAnalysis.technologyImpact'),
        technologyImpact,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Pricing Strategies. ' : t('pdfPages.mappings.marketAnalysis.pricingStrategies'),
        priceStrategies,
      ),
    ],
    // Competitive Analysis
    [
      getMagazineTextObject(
        isExamplePage ? 'Competitor Identification. ' : t('pdfPages.mappings.marketAnalysis.competitorIdentification'),
        competitors,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Customer Acquisition. ' : t('pdfPages.mappings.marketAnalysis.customerAcquisition'),
        attractingCompetitorsCustomers,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Product Differentiation. ' : t('pdfPages.mappings.marketAnalysis.productDifferentiation'),
        competitorsDifference,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Customer Motivation. ' : t('pdfPages.mappings.marketAnalysis.customerMotivation'),
        purchasingProducts,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Future Competition. ' : t('pdfPages.mappings.marketAnalysis.futureCompetition'),
        futureExpectations,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Entry Barriers. ' : t('pdfPages.mappings.marketAnalysis.entryBarriers'),
        barriers,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Market Disruptors. ' : t('pdfPages.mappings.marketAnalysis.marketDisruptors'),
        changingOfCurrentMarket,
      ),
    ],
    // Consumer Needs
    [
      getMagazineTextObject(
        isExamplePage ? 'Purchase Decisions. ' : t('pdfPages.mappings.marketAnalysis.purchaseDecisions'),
        purchasingDecision,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Influencing Factors. ' : t('pdfPages.mappings.marketAnalysis.influencingFactors'),
        influencingFactors,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Behavioral Difference. ' : t('pdfPages.mappings.marketAnalysis.behavioralDifference'),
        differences,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Market Satisfaction. ' : t('pdfPages.mappings.marketAnalysis.marketSatisfaction'),
        howCustomersSatisfied,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Demand Creation. ' : t('pdfPages.mappings.marketAnalysis.demandCreation'),
        demand,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Customer Base Size. ' : t('pdfPages.mappings.marketAnalysis.customerBaseSize'),
        customerBaseSize,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Product Launch Feedback. ' : t('pdfPages.mappings.marketAnalysis.productLaunchFeedback'),
        survey,
      ),
    ],
  ];
};

export const getMarketTrends = (businessPlanChapters: IBusinessToolForm) => {
  const {
    marketOverview: { marketTrends },
  } = businessPlanChapters.MARKET_ANALYSIS;
  const colors = getColorStylesByRecord(colorIndexesMarketTrends);

  return marketTrends?.map((el, i) => ({ ...el, color: colors[i] })) || [];
};

export const useGetConsumptionForecast = (
  businessPlanChapters: IBusinessToolForm,
  isExamplePage: boolean,
): IChartWithBars[] => {
  const { t, i18n } = useTranslation();

  return useMemo(() => {
    const { forecastedConsumptionChart } = businessPlanChapters.MARKET_ANALYSIS.marketOverview;
    const { currency } = businessPlanChapters.GLOBAL;
    const type = getColorType();
    const colors = getColorStyles(colorIndexesByPalleteForecast[type]);

    const additionForFuturePeriods = isExamplePage
      ? 'f'
      : i18n.language === 'uk'
        ? 'пр.'
        : i18n.language === 'de'
          ? 'f'
          : 'f';

    return (
      forecastedConsumptionChart.map(({ title, source, units, volumePeriods, tableHeadings }) => ({
        title,
        colors,
        source,
        unit:
          units.selectedUnits === 'Volume in'
            ? `${t(`justUnits.${units.volume}`) ?? 'pcs'} ${units.currency === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(units.currency) : t(`pdfPages.mappings.financialPlan.dimensionality.${units.currency}`)}`
            : `${currency ? currency.symbol : ''} ${units.currency === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(units.currency) : t(`pdfPages.mappings.financialPlan.dimensionality.${units.currency}`)}`,
        datasetsLabels: volumePeriods.map((item) => item?.[REGION_PERIODS_NAMES.REGION] || ''),
        ...Object.entries(tableHeadings).reduce<{ labels: string[]; data: number[][] }>(
          (result, [key, value]) => {
            if (key === 'region') {
              return result;
            }
            const currentYear = new Date().getFullYear();
            result.labels.push(value <= currentYear ? value : `${value} ${additionForFuturePeriods}`);
            volumePeriods.forEach((item, index) => {
              const current = Number(item[key as keyof IRegionConsumptionRow]);

              if (result.data[index]) {
                result.data[index].push(current);
              } else {
                result.data.push([current]);
              }
            });
            return result;
          },
          {
            labels: [],
            data: [],
          },
        ),
      })) || []
    );
  }, [t, i18n]);
};

export const useGetStructureDetails = (
  businessPlanChapters: IBusinessToolForm,
  isExamplePage: boolean,
): IDonutWithText[] => {
  const { t, i18n } = useTranslation();

  return useMemo(() => {
    const { currentMarketStructure } = businessPlanChapters.MARKET_ANALYSIS.marketOverview;
    const { currency } = businessPlanChapters.GLOBAL;
    const currencySymbol = getCurrencySymbol(currency);
    const type = getColorType();
    const colors = getColorStyles(colorIndexesStructureDetails[type]);

    return (
      currentMarketStructure.map(({ consumerDetails, date, source, units, title }) => {
        const isVolumeUnit = units.selectedUnits === t('volumeIn');
        const unit = isVolumeUnit ? t(`justUnits.${units.volume}`) : currencySymbol;
        const dimensionality =
          units.currency === DIMENSIONALITY_VALUES.SINGLE
            ? ''
            : t(`pdfPages.mappings.financialPlan.dimensionality.${units.currency}`);
        let total = 0;
        return {
          title,
          source,
          date: formatDateForPDF(date, 'MMMM YYYY', isExamplePage),
          unit,
          colors,
          ...consumerDetails.reduce<{ labels: string[]; percentages: number[]; physicals: number[] }>(
            (result, item) => {
              const physicalValue = formatNumber(Number(isVolumeUnit ? item.physicalShare : item.monetaryShare));
              total += isVolumeUnit
                ? Number(item.physicalShare)
                : Number(item.monetaryShare) * dimensionalityAmount[units.currency as IDimensionalityOption];

              result.labels.push(
                `${item.consumer}${+physicalValue !== 0 ? ` - ${physicalValue} ${dimensionality} ${unit}` : ''} `,
              );
              result.percentages.push(Number(item.percentageShare));
              result.physicals.push(Number(item.physicalShare));

              return result;
            },
            {
              labels: [],
              percentages: [],
              physicals: [],
            },
          ),
          total: total ? `${formatValueWithUnit(unit, formatNumberToShort(total))}` : '',
          isPercentsPriority: true,
        };
      }) || []
    );
  }, [t, i18n]);
};

type DataItem = {
  label: string;
  data: string[];
};

function findZeroIndices(data: DataItem[]): number[] {
  if (data.length === 0 || data[0].data.length === 0) return []; // Handle empty input

  const indices: number[] = [];
  const numCols = data[0].data.length;

  for (let j = 0; j < numCols; j++) {
    let allZeros = true;
    for (let i = 0; i < data.length; i++) {
      if (data[i].data[j] !== '0') {
        allZeros = false;
        break;
      }
    }
    if (allZeros) {
      indices.push(j);
    }
  }

  return indices;
}

export const getPriceOverview = (businessPlanChapters: IBusinessToolForm, isExamplePage: boolean) => {
  const { averagePricesTitle, averagePricesDate, averagePricesCurrency, averagePricesChart } =
    businessPlanChapters.MARKET_ANALYSIS.marketOverview;

  const { averagePricesRegionsHeadings, averagePricesRegionsRows } = averagePricesChart;
  const { currency } = businessPlanChapters.GLOBAL;
  const currencySymbol = getCurrencySymbol(currency);
  const type = getColorType();
  const colors = getColorStyles(colorIndexesPriceOverview[type]);

  const datasets = averagePricesRegionsRows
    .map(({ product, ...items }) => ({
      label: product,
      data: Object.entries(items).reduce((result: string[], [key, value]) => {
        if (!averagePricesRegionsHeadings[key as keyof Omit<IAveragePricesRegionRow, 'product'>]) {
          return result;
        } else {
          result.push(value);
          return result;
        }
      }, []),
    }))
    .filter(({ data }) => data.length > 0);

  const foundedZerosIndexes = findZeroIndices(datasets);
  const preparedDatasets =
    foundedZerosIndexes.length === 0
      ? datasets
      : datasets.map(({ label, data }) => {
          const filteredData = data.filter((_, index) => !foundedZerosIndexes.includes(index));
          return { label, data: filteredData };
        });

  const totals = Object.keys(averagePricesRegionsHeadings).map((key) => {
    if (!averagePricesRegionsHeadings[key as keyof Omit<IAveragePricesRegionRow, 'product'>]) {
      return 0;
    } else {
      // @ts-expect-error no types
      return averagePricesRegionsRows.reduce((result, row) => result + Number(row[key]), 0);
    }
  });

  const labels: string[] = Object.entries(averagePricesRegionsHeadings).reduce((result: string[], [key, value]) => {
    // @ts-expect-error no types
    if (value && averagePricesRegionsRows.reduce((result, row) => result + Number(row[key]), 0) !== 0) {
      result.push(value);
      return result;
    } else {
      return result;
    }
  }, []);

  return {
    title: averagePricesTitle,
    subtitle: formatDateForPDF(averagePricesDate, 'MMMM YYYY', isExamplePage),
    labels: labels,
    unit: `${currencySymbol} ${isExamplePage ? getDimensionalityForExamplePage(averagePricesCurrency) : t(`pdfPages.mappings.financialPlan.dimensionality.${averagePricesCurrency}`)}`,
    datasets: preparedDatasets,
    totals,
    colors,
  };
};

export const getCompetitorsComparison = (businessPlanChapters: IBusinessToolForm, isExamplePage: boolean) => {
  const { keyCompetitors } = businessPlanChapters.MARKET_ANALYSIS.competitiveAnalysis;
  const {
    AddressOfRegistrationIcon,
    AnnualSalesIcon,
    BriefProductDescriptionIcon,
    ContactPersonIcon,
    LogoFourChapterIcon,
    MarketShareIcon,
    NameChapterFourIcon,
    PositioningIcon,
    PricingPolicyIcon,
    SalesChannelsIcon,
    WebsiteIcon,
  } = icons[getColorType()];

  const { currency } = businessPlanChapters.GLOBAL;
  const currencySymbol = getCurrencySymbol(currency);

  return keyCompetitors.reduce<IStripedTableRow[]>(
    (result, competitor) => {
      result.forEach((row) => {
        switch (row.id) {
          case 'customerLoyaltyLevel': {
            row.info.push(
              isExamplePage
              ? getPriorityForExamplePage(
                t(
                      // @ts-expect-error no types
                      `levels.${(competitor.customerLoyaltyLevel?.value || competitor.customerLoyaltyLevel).toLowerCase()}`,
                    ),
                  )
                : t(
                  // @ts-expect-error no types
                    `levels.${(competitor.customerLoyaltyLevel?.value || competitor.customerLoyaltyLevel).toLowerCase()}`,
                  ),
            );
            break;
          }
          case 'file': {
            row.info.push(competitor.file?.[0]?.url || '');
            row.imgValueSettings = { height: '100px' };
            break;
          }
          case 'annualSalesVolume': {
            row.info.push(
              +competitor.annualSalesVolume ? formatCurrency(+competitor.annualSalesVolume, currencySymbol) : '',
            );
            break;
          }
          case 'marketShare': {
            row.info.push(formatPercentage(competitor.marketShare));
            break;
          }
          default:
            row.info.push(competitor[row.id as keyof Omit<IKeyCompetitors, 'file'>]);
        }
      });
      return result;
    },
    [
      {
        id: 'competitor',
        imgSrc: NameChapterFourIcon,
        heading: isExamplePage ? 'Name' : t('pdfPages.mappings.marketAnalysis.name'),
        info: [],
      },
      {
        id: 'file',
        imgSrc: LogoFourChapterIcon,
        heading: isExamplePage ? 'Logo' : t('pdfPages.mappings.marketAnalysis.logo'),
        info: [],
      },
      {
        id: 'location',
        imgSrc: AddressOfRegistrationIcon,
        heading: isExamplePage ? 'Location' : t('pdfPages.mappings.marketAnalysis.location'),
        info: [],
      },
      {
        id: 'website',
        imgSrc: WebsiteIcon,
        heading: isExamplePage ? 'Website' : t('pdfPages.mappings.marketAnalysis.website'),
        info: [],
      },
      {
        id: 'description',
        imgSrc: BriefProductDescriptionIcon,
        heading: isExamplePage
          ? 'Brief Description of the Product'
          : t('pdfPages.mappings.marketAnalysis.briefDescriptionOfTheProduct'),
        info: [],
      },
      {
        id: 'positioning',
        imgSrc: PositioningIcon,
        heading: isExamplePage ? 'Positioning' : t('pdfPages.mappings.marketAnalysis.positioning'),
        info: [],
      },
      {
        id: 'salesChannels',
        imgSrc: SalesChannelsIcon,
        heading: isExamplePage ? 'Sales Channels' : t('pdfPages.mappings.marketAnalysis.salesChannels'),
        info: [],
      },
      {
        id: 'pricingPolicy',
        imgSrc: PricingPolicyIcon,
        heading: isExamplePage ? 'Pricing Policy' : t('pdfPages.mappings.marketAnalysis.pricingPolicy'),
        info: [],
      },
      {
        id: 'annualSalesVolume',
        imgSrc: AnnualSalesIcon,
        heading: isExamplePage ? 'Annual Sales Volume' : t('pdfPages.mappings.marketAnalysis.annualSalesVolume'),
        info: [],
      },
      {
        id: 'marketShare',
        imgSrc: MarketShareIcon,
        heading: isExamplePage ? 'Market Share' : t('pdfPages.mappings.marketAnalysis.marketShare'),
        info: [],
      },
      {
        id: 'customerLoyaltyLevel',
        imgSrc: ContactPersonIcon,
        heading: isExamplePage ? 'Customer Loyalty Level' : t('pdfPages.mappings.marketAnalysis.customerLoyaltyLevel'),
        info: [],
      },
    ],
  );
};

export const getProductComparison = (businessPlanChapters: IBusinessToolForm, isExamplePage: boolean) => {
  const { comparisonItem } = businessPlanChapters.MARKET_ANALYSIS.competitiveAnalysis;

  return comparisonItem.reduce<IFeaturesTableRowItem[][]>((result, item) => {
    const firstRowsData = { files: [], names: [] };
    const { names, files } =
      item.competitorFeaturesList?.reduce<{
        names: string[];
        files: (string | IFeaturesTableImageInfo)[];
      }>((result, { file, name }) => {
        result.names.push(name);
        result.files.push({ imgSrc: file?.[0]?.url || '' });

        return result;
      }, firstRowsData) || firstRowsData;

    result.push([
      {
        id: 'products',
        heading:
          item.product || isExamplePage ? 'Product/Service name' : t('pdfPages.mappings.marketAnalysis.prodServName'),
        items: [item?.name || '', ...names],
      },
      {
        id: 'features',
        heading: isExamplePage ? 'Features' : t('pdfPages.mappings.marketAnalysis.features'),
        // @ts-expect-error no types
        items: [{ imgSrc: item?.file?.[0]?.url || '' }, ...files],
      },
      ...(item.featuresForComparison?.map(({ feature }, index) => ({
        id: feature,
        heading: feature,
        items: [
          // @ts-expect-error no types
          item.feature[index] ?? '',
          // @ts-expect-error no types
          ...(item.competitorFeaturesList?.map((listItem) => listItem.feature[index] ?? '') || []),
        ],
      })) || []),
    ]);

    return result;
  }, []);
};

export const getStrengthAndWeakness = (businessPlanChapters: IBusinessToolForm, isExamplePage: boolean) => {
  const { competitorsQualities, yourCompanyQualities } = businessPlanChapters.MARKET_ANALYSIS.competitiveAnalysis;
  const { StrengthFourIcon, WeaknessFourIcon } = icons[getColorType()];

  return competitorsQualities.reduce<IPointTableProps>(
    (result, { name, file, strength, weaknesses }) => {
      result.headers.push({ name, image: (file && file[0]?.url) || '' });
      result.rows[0].items.push(strength.map(({ strength }) => strength).filter((strength) => !isEmpty(strength)));
      result.rows[1].items.push(
        weaknesses.map(({ weaknesses }) => weaknesses).filter((weaknesses) => !isEmpty(weaknesses)),
      );

      return result;
    },
    {
      headers: [{ name: yourCompanyQualities?.name || '', image: yourCompanyQualities?.file?.[0]?.url || '' }],
      rows: [
        {
          pointName: isExamplePage ? 'Strength' : t('strength'),
          iconSrc: StrengthFourIcon,
          items: [yourCompanyQualities[COMPANY_QUALITIES_NAMES.STRENGTH]?.map(({ strength }) => strength)],
        },
        {
          pointName: isExamplePage ? 'Weakness' : t('weakness'),
          iconSrc: WeaknessFourIcon,
          items: [yourCompanyQualities[COMPANY_QUALITIES_NAMES.WEAKNESSES]?.map(({ weaknesses }) => weaknesses)],
        },
      ],
    },
  );
};

export const getB2C = (businessPlanChapters: IBusinessToolForm, isExamplePage: boolean) => {
  const { stage, age, annualIncome, profession, personalInterests, purchasingHabits, needsAndProblems } =
    businessPlanChapters.MARKET_ANALYSIS.consumerNeeds.B2C;

  const { currency } = businessPlanChapters.GLOBAL;
  const currencySymbol = getCurrencySymbol(currency);

  const {
    AgeIcon,
    GenderIcon,
    IncomeIcon,
    NeedsAndProblemsIcon,
    PersonalInterestsIcon,
    ProfessionIcon,
    PurchasingHabitsIcon,
  } = icons[getColorType()];

  return [
    {
      id: 'stage',
      
      info: isExamplePage
      // @ts-expect-error no types
        ? getGenderForExamplePage(stage?.value?.toLowerCase() || stage?.toLowerCase())
        // @ts-expect-error no types
        : t(`genderOptions.${stage?.value?.toLowerCase() || stage?.toLowerCase()}`),
      title: isExamplePage ? 'Gender' : t('marketAnalysisForm.consumers.gender'),
      iconSrc: GenderIcon,
    },
    {
      id: 'startDate',
      info: age,
      title: isExamplePage ? 'Age' : t('marketAnalysisForm.consumers.age'),
      iconSrc: AgeIcon,
    },
    {
      id: 'expectedDeadline',
      info: Number(annualIncome) ? formatCurrency(+annualIncome, currencySymbol) : '',
      title: isExamplePage ? 'Income' : t('marketAnalysisForm.consumers.income'),
      iconSrc: IncomeIcon,
    },
    {
      id: 'profession',
      info: profession,
      title: isExamplePage ? 'Profession' : t('marketAnalysisForm.consumers.profession'),
      iconSrc: ProfessionIcon,
    },
    {
      id: 'personalInterests',
      info: personalInterests,
      title: isExamplePage ? 'Personal Interests' : t('pdfPages.mappings.marketAnalysis.personalInterests'),
      iconSrc: PersonalInterestsIcon,
    },
    {
      id: 'purchasingHabits',
      info: purchasingHabits,
      title: isExamplePage ? 'Purchasing Habits' : t('pdfPages.mappings.marketAnalysis.purchasingHabits'),
      iconSrc: PurchasingHabitsIcon,
    },
    {
      id: 'needsAndProblems',
      info: needsAndProblems,
      title: isExamplePage ? 'Needs and Problems' : t('pdfPages.mappings.marketAnalysis.needsAndProblems'),
      iconSrc: NeedsAndProblemsIcon,
    },
  ].filter(({ info }) => info);
};

export const getB2B = (businessPlanChapters: IBusinessToolForm, isExamplePage: boolean) => {
  const { industry, annualRevenue, numberOfEmployers, region, customerBuyingBehavior } =
    businessPlanChapters.MARKET_ANALYSIS.consumerNeeds.B2B;
  const { AdressSixIcon, AnnualRevenueIcon, IndustryIcon, NumberOfEmployeesIcon, PurchasingCycleIcon } =
    icons[getColorType()];

  const { currency } = businessPlanChapters.GLOBAL;
  const currencySymbol = getCurrencySymbol(currency);

  return [
    { id: 'industry', info: industry, title: isExamplePage ? 'Industry' : t('industry'), iconSrc: IndustryIcon },
    {
      id: 'annualRevenue',
      info: Number(annualRevenue) ? formatCurrency(+annualRevenue, currencySymbol) : '',
      title: isExamplePage ? 'Annual Revenue' : t('marketAnalysisForm.consumers.annualRevenue'),
      iconSrc: AnnualRevenueIcon,
    },
    {
      id: 'numberOfEmployers',
      info: Number(numberOfEmployers) ? numberOfEmployers.toString() : '',
      title: isExamplePage ? 'Number of Employees' : t('pdfPages.mappings.marketAnalysis.numberOfEmployers'),
      iconSrc: NumberOfEmployeesIcon,
    },
    {
      id: 'region',
      info: region,
      title: isExamplePage ? 'Region' : t('marketAnalysisForm.consumers.region'),
      iconSrc: AdressSixIcon,
    },
    {
      id: 'purchasingCycle',
      info: customerBuyingBehavior,
      title: isExamplePage ? 'Purchasing Cycle' : t('pdfPages.mappings.marketAnalysis.purchasingCycle'),
      iconSrc: PurchasingCycleIcon,
    },
  ].filter(({ info }) => info);
};

export const getIsCompetitorsEmpty = (competitorsComparisonData: IStripedTableRow[]) => {
  if (!competitorsComparisonData?.length) {
    return true;
  }

  for (const row of competitorsComparisonData) {
    const value = row.info[0];

    switch (row.id) {
      case 'customerLoyaltyLevel':
        if (value && value !== 'High') {
          return false;
        }
        break;
      case 'annualSalesVolume':
        if (value && value !== '$') {
          return false;
        }
        break;
      default:
        if (typeof value === 'string') {
          if (value.length > 0) {
            return false;
          }
        }
        if (typeof value === 'object') {
          if (value.value.length > 0) {
            return false;
          }
        }
        return true;
    }
  }

  return true;
};

export const getIsStrengthAndWeaknessEmpty = (data: IPointTableProps) => {
  const { headers, rows } = data;
  if (
    headers.filter(({ name, image }) => name || image).length === 0 ||
    rows.filter(({ items }) => items.filter((item) => item.length > 0).length > 0).length === 0
  ) {
    return true;
  } else {
    return false;
  }
};

export const getIsMarketTrendsEmpty = (data: IMarketTrendsForm[]) => {
  if (
    data.some(
      (item) => item[MARKET_TRENDS_FIELD_NAMES.TREND_CHARACTERISTICS] || item[MARKET_TRENDS_FIELD_NAMES.TREND_NAME],
    )
  ) {
    return false;
  }
  return true;
};
